<template>
  <v-dialog
    :value="value"
    fullscreen
    persistent
    transition="dialog-bottom-transition"
    data-test-id="message-preview-sheet"
    @input="$emit('input', $event)"
  >
    <v-card class="sheet" rounded="0" elevation="0">
      <DrawerToolbar color="warning" icon="mdi-eye" title="Preview Mode" @close="close" />

      <v-tabs
        v-model="activeTab"
        color="brandPrimary"
        centered
        show-arrows
        class="tabs"
        data-test-id="message-preview-tabs"
      >
        <v-tab
          v-for="tab in tabs"
          :key="tab.method"
          :href="`#${tab.method}`"
          :data-test-id="`message-preview-tab-${tab.method}`"
        >
          {{ tab.label }}
        </v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" class="tabs-items">
        <v-tab-item
          v-for="tab in tabs"
          :key="tab.method"
          :value="tab.method"
          :transition="false"
          :reverse-transition="false"
        >
          <div class="content-frame" :class="`content-frame--${tab.method}`">
            <div v-if="previews[tab.method].loading" class="state">
              <Typography variant="body-2">Loading message preview...</Typography>
              <v-progress-circular color="brandPrimary" indeterminate :size="55" :width="4" />
            </div>

            <Typography
              v-else-if="previews[tab.method].error"
              variant="body-2"
              class="state error--text"
              data-test-id="message-preview-error"
            >
              {{ previews[tab.method].error }}
            </Typography>

            <div
              v-else-if="tab.method === 'activity' && previews.activity.content"
              class="activity-content"
            >
              <ActivityFeedCard
                :key="previews.activity.content.messageLogId"
                :id="previews.activity.content.contextResourceId"
                :messageId="previews.activity.content.messageId"
                :icon="previews.activity.content.icon"
                :iconColor="previews.activity.content.iconColor"
                :context="previews.activity.content.context"
                :subcontext="previews.activity.content.subcontext"
                :title="previews.activity.content.cardTitle"
                :date="previews.activity.content.date"
                :messageTitle="previews.activity.content.messageSubject"
                :messageSubtitle="previews.activity.content.messageShortBody"
                :messageBody="previews.activity.content.messageBody"
                :messageSuccessImage="previews.activity.content.successImage"
                :image="previews.activity.content.imageURL"
                :linkText="previews.activity.content.linkText"
                :object="previews.activity.content"
                :messageTitleFlag="previews.activity.content.titleFlag"
                :messageSubtitleFlag="previews.activity.content.subtitleFlag"
                :messageIconFlag="previews.activity.content.messageIconFlag"
                :robinFlag="false"
                :imageFlag="previews.activity.content.displayImageInCardPreview"
                :progressFlag="previews.activity.content.progressFlag"
                :likedByUser="previews.activity.content.likedByUser"
                :linkButtonFlag="true"
                :fullMessage="previews.activity.content.context === 'recognition'"
                :comments="previews.activity.content.comments"
                :reactions="previews.activity.content.reactions"
                :values="previews.activity.content.values"
                :recipients="previews.activity.content.recipients"
                :privateRecognition="previews.activity.content.privateRecognition"
                :newMessage="!previews.activity.content.readOnDashboard"
                @create-comment="previews.activity.content.comments.push($event)"
              />
            </div>

            <div
              v-else-if="previews[tab.method].content"
              class="html-content"
              :class="{ 'html-content--email': tab.method === 'email' }"
              v-html="previews[tab.method].content"
            />
          </div>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </v-dialog>
</template>

<script>
import CommunicationService from "@/services/CommunicationService";

import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Typography from "@/components/generic/Typography.vue";
import ActivityFeedCard from "@/components/activity/ActivityFeedCardV2.vue";

const TABS = [
  { method: "email", label: "Email" },
  { method: "sms", label: "Text Message" },
  { method: "push", label: "Push Notification" },
  { method: "activity", label: "Activity Page" },
];

const blankPreviewState = () => ({
  email: { content: null, loading: false, error: null, fetched: false },
  sms: { content: null, loading: false, error: null, fetched: false },
  push: { content: null, loading: false, error: null, fetched: false },
  activity: { content: null, loading: false, error: null, fetched: false },
});

export default {
  name: "MessagePreviewSheet",
  components: {
    DrawerToolbar,
    Typography,
    ActivityFeedCard,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    messageSubject: {
      type: String,
      default: "",
    },
    messageShortBody: {
      type: String,
      default: "",
    },
    messageBody: {
      type: String,
      default: "",
    },
    context: {
      type: String,
      default: null,
    },
    subcontext: {
      type: String,
      default: null,
    },
    contextResourceId: {
      type: Number,
      default: null,
    },
    imageURL: {
      type: String,
      default: null,
    },
    clientId: {
      type: Number,
      default: null,
    },
    // Which tab to show first when the sheet opens.
    initialMethod: {
      type: String,
      default: "email",
      validator: (v) => ["email", "sms", "push", "activity"].includes(v),
    },
  },
  emits: ["input"],
  data() {
    return {
      tabs: TABS,
      activeTab: this.initialMethod,
      // Per-method preview cache. `fetched` prevents re-firing the request
      // when the user switches tabs back to one that already loaded.
      previews: blankPreviewState(),
    };
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        // Reset the cache on every open so edits made in the parent sheet
        // between preview sessions are always reflected.
        this.previews = blankPreviewState();
        this.activeTab = this.initialMethod;
        this.loadPreview(this.activeTab);
      }
    },
    activeTab(method) {
      if (this.value) this.loadPreview(method);
    },
  },
  methods: {
    close() {
      this.$emit("input", false);
    },
    async loadPreview(method) {
      const slot = this.previews[method];
      if (!slot || slot.fetched || slot.loading) return;
      slot.loading = true;
      slot.error = null;
      try {
        const body = {
          messageSubject: this.messageSubject,
          messageShortBody: this.messageShortBody,
          messageBody: this.messageBody,
          contextResourceId: this.contextResourceId,
          context: this.context,
          subcontext: this.subcontext,
          clientId: this.clientId,
          imageURL: this.imageURL,
        };
        slot.content = await CommunicationService.previewMessage(method, body);
        slot.fetched = true;
      } catch (err) {
        slot.error = "There was an issue previewing the message";
      } finally {
        slot.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.sheet {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.tabs {
  flex: 0 0 auto;
  padding-top: 24px;
}

.tabs-items {
  flex: 1 1 auto;
  background-color: transparent;
}

.content-frame {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 24px 16px 48px;
  width: 100%;
}

.content-frame--email {
  max-width: 640px;
  margin-left: auto;
  margin-right: auto;
}

.content-frame--sms {
  max-width: 420px;
  margin-left: auto;
  margin-right: auto;
}

.content-frame--push {
  max-width: 380px;
  margin-left: auto;
  margin-right: auto;
}

.content-frame--activity {
  max-width: 520px;
  margin-left: auto;
  margin-right: auto;
}

.state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding-top: 24px;
}

.html-content {
  width: 100%;
  text-align: left;
}

.html-content--email {
  pointer-events: none;
}

.activity-content {
  width: 100%;
}
</style>

<style>
/* Match the existing MessagePreviewDrawer: cap the email template's inner
   .email-content (server ships it at 600px) so it fits the preview frame. */
.html-content--email .email-content {
  width: 100% !important;
  max-width: 600px !important;
}
</style>
